import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getNotes, createNote } from '@/services/api';
import { PlusIcon } from '@heroicons/react/24/outline';

export default function InternalNotesPanel({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient();
  const [content, setContent] = useState('');

  const { data: notes, isLoading } = useQuery({
    queryKey: ['notes', ticketId],
    queryFn: () => getNotes(ticketId),
  });

  const addNote = useMutation({
    mutationFn: () => createNote(ticketId, content.trim(), 1),
    onSuccess: () => {
      setContent('');
      qc.invalidateQueries({ queryKey: ['notes', ticketId] });
    },
  });

  const sortedNotes = [...(notes ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const handleSubmit = () => {
    if (!content.trim() || addNote.isPending) {
      return;
    }
    addNote.mutate();
  };

  return (
    <div className="rounded-xl border border-brand-border bg-white p-4 shadow-sm xl:sticky xl:top-4">
      <h3 className="mb-1 text-base font-semibold text-brand-text-primary">Internal Notes</h3>
      <p className="mb-3 text-xs text-gray-500">
        These notes are internal only and not visible to the customer.
      </p>

      <div className="mb-4 rounded-xl border border-brand-border bg-brand-surface p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-brand-text-secondary">Add an internal note</p>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || addNote.isPending}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-accent text-brand-text-inverse transition hover:bg-brand-accent/90 disabled:opacity-40"
            title="Add note"
            aria-label="Add note"
          >
            <PlusIcon className="h-4 w-4" title="Add note" />
          </button>
        </div>

        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Add a note…"
          rows={2}
          className="w-full resize-none rounded-md border border-brand-border px-3 py-2 text-sm"
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && content.trim() && !addNote.isPending) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <p className="text-xs text-gray-400">Tip: Press Enter to add. Use Shift + Enter for a new line.</p>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}

      {!isLoading && sortedNotes.length === 0 && (
        <p className="rounded-lg border border-dashed border-brand-border bg-brand-surface p-3 text-sm text-brand-text-secondary">
          No internal notes yet.
        </p>
      )}

      <div className="max-h-[28rem] space-y-2 overflow-y-auto pr-1">
        {sortedNotes.map((n) => (
          <article key={n.noteId} className="flex justify-start">
            <div className="w-full rounded-2xl border border-brand-border bg-white px-3 py-2 shadow-sm">
              <p className="whitespace-pre-wrap text-sm text-brand-text-primary">{n.content}</p>
              <p className="mt-1 text-xs text-gray-400">{new Date(n.createdAt).toLocaleString()}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
